"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import type { ReactNode } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_LABEL, FIELD_MONO, FIELD_UNDERLINE } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { Plus, X } from "lucide-react"

const CONTROL = cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")
const AMOUNT = cn(FIELD_UNDERLINE, FIELD_MONO, "pb-[7px]")
const TRIGGER = cn(CONTROL, "h-auto")

/**
 * A part of the package that most offers do not have.
 *
 * Bonus, equity and notes were three copies of the same twelve lines — a ghost
 * button in `text-blue-600` that swapped itself for a labelled block with its
 * own dismiss. One shape, stated once, in the app's own colours.
 */
function OptionalSection({
  label,
  shown,
  onShow,
  onHide,
  children,
}: {
  label: string
  shown: boolean
  onShow: () => void
  onHide: () => void
  children: ReactNode
}) {
  if (!shown) {
    return (
      <button
        type="button"
        onClick={onShow}
        className="mt-5 flex w-full items-center justify-between border-b pb-[7px] text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
      >
        {label}
        <Plus className="size-3.5" />
      </button>
    )
  }

  return (
    <div className="mt-5">
      <div className="flex items-baseline justify-between">
        <span className={FIELD_LABEL}>{label}</span>
        <button
          type="button"
          onClick={onHide}
          aria-label={`Remove ${label.toLowerCase()}`}
          className="text-muted-foreground transition-colors hover:text-destructive"
        >
          <X className="size-3" />
        </button>
      </div>
      <div className="mt-[9px]">{children}</div>
    </div>
  )
}

interface CompensationData {
  salary: {
    amount: string
    currency: string
    frequency: string
  }
  bonus?: {
    amount: string
    currency: string
    frequency: string
  }
  equity?: {
    amount: string
    type: string
  }
  notes?: string
}

interface CompensationDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: CompensationData) => void
  initialData?: CompensationData | null
}

export function CompensationDialog({ isOpen, onClose, onSave, initialData }: CompensationDialogProps) {
  const [showBonus, setShowBonus] = useState(!!initialData?.bonus)
  const [showEquity, setShowEquity] = useState(!!initialData?.equity)
  const [showNotes, setShowNotes] = useState(!!initialData?.notes)

  const { register, handleSubmit, setValue, reset } = useForm<CompensationData>({
    defaultValues: initialData || {
      salary: {
        amount: "",
        currency: "USD",
        frequency: "Yearly",
      },
    },
  })

  const onSubmit = (data: CompensationData) => {
    // Clean up data - remove empty optional fields
    const cleanData: CompensationData = {
      salary: data.salary,
    }

    if (showBonus && data.bonus?.amount) {
      cleanData.bonus = data.bonus
    }

    if (showEquity && data.equity?.amount) {
      cleanData.equity = data.equity
    }

    if (showNotes && data.notes?.trim()) {
      cleanData.notes = data.notes
    }

    onSave(cleanData)
    onClose()
  }

  const handleClose = () => {
    reset()
    setShowBonus(!!initialData?.bonus)
    setShowEquity(!!initialData?.equity)
    setShowNotes(!!initialData?.notes)
    onClose()
  }

  return (
    <DialogShell
      open={isOpen}
      onOpenChange={handleClose}
      title="Compensation"
      footer={
        <PageAction tone="primary" type="submit" form="compensation-form">
          Save
        </PageAction>
      }
    >
      <form id="compensation-form" onSubmit={handleSubmit(onSubmit)}>
        <div className={FIELD_LABEL}>Salary</div>
        <div className="mt-[9px] flex items-baseline gap-3">
          <Select defaultValue="USD" onValueChange={(value) => setValue("salary.currency", value)}>
            <SelectTrigger aria-label="Currency" className={cn(TRIGGER, "w-[74px] flex-none")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              <SelectItem value="CAD">CAD</SelectItem>
            </SelectContent>
          </Select>
          <input
            {...register("salary.amount", { required: true })}
            placeholder="200000"
            aria-label="Salary amount"
            className={cn(AMOUNT, "min-w-0 flex-1")}
          />
          <Select defaultValue="Yearly" onValueChange={(value) => setValue("salary.frequency", value)}>
            <SelectTrigger aria-label="Frequency" className={cn(TRIGGER, "w-[92px] flex-none")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yearly">Yearly</SelectItem>
              <SelectItem value="Monthly">Monthly</SelectItem>
              <SelectItem value="Hourly">Hourly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <OptionalSection
          label="Bonus"
          shown={showBonus}
          onShow={() => setShowBonus(true)}
          onHide={() => setShowBonus(false)}
        >
          <div className="flex items-baseline gap-3">
            <Select defaultValue="$" onValueChange={(value) => setValue("bonus.currency", value)}>
              <SelectTrigger aria-label="Bonus unit" className={cn(TRIGGER, "w-[62px] flex-none")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="$">$</SelectItem>
                <SelectItem value="€">€</SelectItem>
                <SelectItem value="£">£</SelectItem>
                <SelectItem value="%">%</SelectItem>
              </SelectContent>
            </Select>
            <input
              {...register("bonus.amount")}
              placeholder="Amount"
              aria-label="Bonus amount"
              className={cn(AMOUNT, "min-w-0 flex-1")}
            />
            <Select defaultValue="Annual" onValueChange={(value) => setValue("bonus.frequency", value)}>
              <SelectTrigger aria-label="Bonus frequency" className={cn(TRIGGER, "w-[92px] flex-none")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Annual">Annual</SelectItem>
                <SelectItem value="Quarterly">Quarterly</SelectItem>
                <SelectItem value="Monthly">Monthly</SelectItem>
                <SelectItem value="One-time">One-time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </OptionalSection>

        <OptionalSection
          label="Equity"
          shown={showEquity}
          onShow={() => setShowEquity(true)}
          onHide={() => setShowEquity(false)}
        >
          <div className="flex items-baseline gap-3">
            <input
              {...register("equity.amount")}
              placeholder="0.5"
              aria-label="Equity amount"
              className={cn(AMOUNT, "min-w-0 flex-1")}
            />
            <Select defaultValue="%" onValueChange={(value) => setValue("equity.type", value)}>
              <SelectTrigger aria-label="Equity type" className={cn(TRIGGER, "w-[92px] flex-none")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="%">%</SelectItem>
                <SelectItem value="shares">Shares</SelectItem>
                <SelectItem value="options">Options</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </OptionalSection>

        <OptionalSection
          label="Notes"
          shown={showNotes}
          onShow={() => setShowNotes(true)}
          onHide={() => setShowNotes(false)}
        >
          <textarea
            {...register("notes")}
            placeholder="Additional compensation details..."
            rows={3}
            aria-label="Notes"
            className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
          />
        </OptionalSection>
      </form>
    </DialogShell>
  )
}
